/**
 * TabHeader — locked-geometry tab header.
 *
 * Cycle 109 v2 Sprint 1 (S1.3). Ports the canonical primitive from
 * `output/design_reference/design_handoff_echelon_2026_04_26/workspace/primitives.jsx:276-364`
 * into production TypeScript.
 *
 * The geometry is frozen in `PANEL_HEADER_GEOM`. Callers may NOT pass
 * `style` overrides or a `geom` escape hatch — only the documented props.
 * This is what makes module-canvas tiles line up visually across the grid.
 *
 * Reference: SDD §5.1 D-1.3, FR-2.1.
 */
import type { ReactNode } from 'react';

/** Frozen header geometry per design contract. Standard = A1 (24px); signature = A2 (32px). */
export const PANEL_HEADER_GEOM = {
  // +2px height for a bit more vertical weight under the uppercase titles;
  // +4px bodyOffset so the card title breathes under the tab band.
  standard: { height: 24, bodyOffset: 36, tabInset: 10, padL: 12, padR: 16, labelSize: 9.5, dotSize: 5 },
  signature: { height: 32, bodyOffset: 44, tabInset: 10, padL: 14, padR: 18, labelSize: 10.5, dotSize: 6 },
} as const;

export type TabHeaderVariant = keyof typeof PANEL_HEADER_GEOM;

export interface TabHeaderProps {
  /** Tab label text (uppercase, letter-spaced). */
  label: ReactNode;
  /** A1 (24px) or A2 (32px). */
  variant?: TabHeaderVariant;
  /** Tone-dot color (CSS var or hex). Renders an inline dot before the label. */
  dot?: string;
  /** Optional subtitle (signature variant only). */
  subtitle?: ReactNode;
  /** Optional badge/chip rendered inside the right slot. */
  badge?: ReactNode;
  /** Optional right-side chip (live-status / metadata). */
  right?: ReactNode;
}

/** Render a locked tab header above a panel. Caller controls only the props above. */
export function TabHeader({ label, variant = 'standard', dot, subtitle, badge, right }: TabHeaderProps) {
  const g = PANEL_HEADER_GEOM[variant];
  return (
    <div
      style={{
        position: 'absolute',
        // Respect the panel's top-right notch (14px) so the right chip doesn't overrun it.
        left: 0,
        right: 14,
        top: 0,
        height: g.height,
        display: 'flex',
        alignItems: 'stretch',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      {/* Left: tab label chip — the only thing above the panel top border */}
      <div
        style={{
          pointerEvents: 'auto',
          marginLeft: g.tabInset,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          paddingLeft: g.padL,
          paddingRight: g.padR,
          background: 'color-mix(in oklch, var(--bg-panel) 85%, var(--bg-raised))',
          borderBottom: '1px solid var(--border-strong)',
          borderRight: '1px solid var(--border-strong)',
          // Right side cuts diagonally to echo the panel notch language
          clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 100%, 0 100%)',
          whiteSpace: 'nowrap',
        }}
      >
        {dot && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: g.dotSize,
              height: g.dotSize,
              borderRadius: '50%',
              background: dot,
              boxShadow: `0 0 ${g.dotSize * 2}px ${dot}`,
            }}
          />
        )}
        <span
          className="mono"
          style={{
            fontSize: g.labelSize,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--fg)',
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 14,
          }}
        >
          {label}
          {subtitle && variant === 'signature' && (
            <span style={{ fontSize: 9, color: 'var(--fg-dim)', letterSpacing: '0.08em', fontWeight: 400 }}>
              {subtitle}
            </span>
          )}
        </span>
        {badge && (
          <span style={{ marginLeft: 4, display: 'inline-flex', alignItems: 'center' }}>{badge}</span>
        )}
      </div>
      {/* Middle: nothing — the panel's top border / accent stripe continues visually */}
      <div style={{ flex: 1 }} />
      {/* Right: status chip — anchored to the tab band with its own subtle backplate
          and a mirrored chamfer so it reads as a counterpart to the left tab. */}
      {right && (
        <div
          style={{
            pointerEvents: 'auto',
            display: 'inline-flex',
            alignItems: 'center',
            paddingLeft: 12,
            paddingRight: 10,
            background: 'color-mix(in oklch, var(--bg-panel) 60%, transparent)',
            borderBottom: '1px solid var(--border)',
            borderLeft: '1px solid var(--border)',
            clipPath: 'polygon(10px 0, 100% 0, 100% 100%, 0 100%)',
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            letterSpacing: '0.08em',
            color: 'var(--fg-dim)',
            whiteSpace: 'nowrap',
          }}
        >
          {right}
        </div>
      )}
    </div>
  );
}

export default TabHeader;
